import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuthContext } from "@/contexts/AuthContext";

interface OnlineStatus {
  [userId: string]: {
    isOnline: boolean;
    lastSeen: string | null;
  };
}

export const useMatchPresence = (userIds: string[]) => {
  const { user } = useAuthContext();
  const [onlineStatus, setOnlineStatus] = useState<OnlineStatus>({});

  // Track own presence
  useEffect(() => {
    if (!user) return;

    const channel = supabase.channel("matches-presence", {
      config: {
        presence: {
          key: user.id,
        },
      },
    });

    channel
      .on("presence", { event: "sync" }, () => {
        const state = channel.presenceState();
        const newStatus: OnlineStatus = {};
        
        // Process all present users
        Object.keys(state).forEach((key) => {
          newStatus[key] = {
            isOnline: true,
            lastSeen: new Date().toISOString(),
          };
        });

        // Mark absent users as offline
        userIds.forEach((id) => {
          if (!newStatus[id]) {
            newStatus[id] = {
              isOnline: false,
              lastSeen: onlineStatus[id]?.lastSeen || null,
            };
          }
        });

        setOnlineStatus(newStatus);
      })
      .on("presence", { event: "join" }, ({ key, newPresences }) => {
        setOnlineStatus((prev) => ({
          ...prev,
          [key]: {
            isOnline: true,
            lastSeen: new Date().toISOString(),
          },
        }));
      })
      .on("presence", { event: "leave" }, ({ key }) => {
        setOnlineStatus((prev) => ({
          ...prev,
          [key]: {
            isOnline: false,
            lastSeen: new Date().toISOString(),
          },
        }));
      })
      .subscribe(async (status) => {
        if (status === "SUBSCRIBED") {
          await channel.track({
            user_id: user.id,
            online_at: new Date().toISOString(),
          });
        }
      });

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, userIds.join(",")]);

  const isOnline = useCallback(
    (userId: string) => onlineStatus[userId]?.isOnline ?? false,
    [onlineStatus]
  );

  const getLastSeen = useCallback(
    (userId: string) => onlineStatus[userId]?.lastSeen ?? null,
    [onlineStatus]
  );

  return {
    onlineStatus,
    isOnline,
    getLastSeen,
  };
};
